import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "../../components/ui/drawer";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Separator } from "../../components/ui/separator";
import { Download, FileText, User, Building2, Calendar, CheckCircle2, Clock, AlertCircle } from "lucide-react";
import { NoticeOfAssignment } from "../../types/schema";
import { format } from "date-fns";
import { parseLocalDateInput } from "../../lib/dateFormat";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  noa: NoticeOfAssignment;
}

export default function NoAPdfDrawer({ open, onOpenChange, noa }: Props) {
  const getStatusIcon = (status: string) => {
    switch (status) {
      case "Signed": return <CheckCircle2 className="h-4 w-4 text-green-600" />;
      case "Approved": return <CheckCircle2 className="h-4 w-4 text-blue-600" />;
      case "Submit": return <Clock className="h-4 w-4 text-yellow-600" />;
      case "Draft": return <AlertCircle className="h-4 w-4 text-gray-600" />;
      default: return <AlertCircle className="h-4 w-4 text-gray-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Signed": return "bg-green-100 text-green-800 border-green-200";
      case "Approved": return "bg-blue-100 text-blue-800 border-blue-200";
      case "Submit": return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "Draft": return "bg-gray-100 text-gray-800 border-gray-200";
      default: return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="h-[85vh] max-h-[85vh] ms-auto w-[95%] rounded-t-xl">
        <DrawerHeader className="border-b bg-muted/30">
          <div className="flex items-center justify-between">
            <div>
              <DrawerTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Notice of Assignment PDF - {noa.noaId}
              </DrawerTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Generated document ready for signature and assignment
              </p>
            </div>
            <Button variant="outline" className="flex items-center gap-2">
              <Download className="h-4 w-4" />
              Download PDF
            </Button>
          </div>
        </DrawerHeader>

        <div className="flex-1 overflow-hidden">
          {/* Top Section - Signature & Status */}
          <div className="p-6 border-b bg-background">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Lender Details */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Building2 className="h-5 w-5" />
                    Lender Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <div className="text-sm text-muted-foreground">Organization</div>
                    <div className="font-medium">{noa.lender}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Assignment Date</div>
                    <div className="font-medium">{format(parseLocalDateInput(noa.assignmentDate), "MMMM dd, yyyy")}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Document Status</div>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(noa.status)}
                      <Badge className={getStatusColor(noa.status)}>
                        {noa.status}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Customer Details */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <User className="h-5 w-5" />
                    Customer Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <div className="text-sm text-muted-foreground">Company Name</div>
                    <div className="font-medium">{noa.customer.name}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">GSTIN</div>
                    <div className="font-medium">{noa.customer.gstin}</div>
                  </div>
                  {noa.customer.authorizedSignatory && (
                    <div>
                      <div className="text-sm text-muted-foreground">Authorized Signatory</div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{noa.customer.authorizedSignatory.name}</span>
                        <Badge variant="outline" className={getStatusColor(noa.customer.authorizedSignatory.signatureStatus)}>
                          {noa.customer.authorizedSignatory.signatureStatus}
                        </Badge>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          {/* PDF Viewer Section */}
          <div className="flex-1 p-6">
            <Card className="h-full">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Document Preview
                </CardTitle>
              </CardHeader>
              <CardContent className="h-full">
                <div className="h-full border-2 border-dashed border-muted-foreground/25 rounded-lg flex items-center justify-center bg-muted/10">
                  <div className="text-center">
                    <FileText className="h-16 w-16 mx-auto mb-4 text-muted-foreground/50" />
                    <h3 className="text-lg font-semibold mb-2">NoA PDF Document</h3>
                    <p className="text-muted-foreground mb-4">
                      Notice of Assignment PDF would be displayed here
                    </p>
                    <div className="space-y-2 text-sm text-muted-foreground">
                      <div>• Funding Mode: {noa.fundingMode} - {noa.fundingMode === 'B' ? '100% Base + GST' : noa.fundingMode === 'C' ? '100% Base Only' : '% of Base Only'}</div>
                      <div>• Total Asset Value: ₹{noa.fundingAmountCap.toLocaleString("en-IN")}</div>
                      <div>• PV Total: ₹{noa.pvTotal.toLocaleString("en-IN")}</div>
                      <div>• Selected Periods: {noa.selectedRentals.length}</div>
                    </div>
                    <Separator className="my-4" />
                    <Button variant="outline" className="mt-4">
                      <Download className="h-4 w-4 mr-2" />
                      Download Original PDF
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Footer - Additional Notes */}
          <div className="border-t p-6 bg-muted/30">
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  Last updated: {format(new Date(noa.updatedAt), "MMM dd, yyyy 'at' HH:mm")}
                </div>
              </div>
              <div className="text-sm text-muted-foreground">
                Document ID: {noa.noaId} | Schedule: {noa.scheduleId}
              </div>
            </div>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}